/**
 * error-messages.ts — admin 错误码 → 中文显示文案
 *
 * 契约边界：后端 `code` 是稳定契约（dev-docs 20-错误码字典.md），`message` 仅显示文案。
 * admin 是中文-only UI，按 code 本地化显示（与 P1-api-contract「客户端按 code 分支、不依赖 message」一致）。
 * 未登记的 code 回退后端原 message。
 */

/** 错误码 → 中文显示文案 */
export const ADMIN_ERROR_ZH: Record<string, string> = {
  // ---- 系统码（闭集）----
  VALIDATION_FAILED: '输入校验失败，请检查后重试',
  BAD_REQUEST: '请求有误',
  AUTH_REQUIRED: '请先登录',
  AUTH_INVALID: '登录状态无效，请重新登录',
  FORBIDDEN_OPERATION: '无权执行该操作',
  RESOURCE_NOT_FOUND: '资源不存在',
  RESOURCE_CONFLICT: '操作冲突，请刷新后重试',
  RATE_LIMITED: '操作过于频繁，请稍后再试',
  INTERNAL_ERROR: '服务器内部错误',
  SERVICE_UNAVAILABLE: '服务暂时不可用，请稍后再试',

  // ---- 前端本地码（服务端不返回）----
  // 请求没拿到任何 HTTP 响应：与「服务器返回了失败」区分，指引排查网络而非反复重试 / 疑心密码错
  NETWORK_UNREACHABLE: '无法连接服务器，请检查网络后重试',
  // 超时 / 取消都只停止了**本地等待**，服务端可能已经处理完 —— 文案不能说「未保存」「已撤销」，
  // 那是替服务端做一个前端无从知道的判断，运营据此重试会造成重复写入。
  REQUEST_TIMEOUT: '请求超时，服务端可能仍在处理；请刷新页面确认结果，不要直接重试',
  REQUEST_TIMEOUT_READ: '请求超时，请重试',
  REQUEST_CANCELED: '请求已取消；若是写操作，服务端可能已生效，请刷新页面确认',

  // ---- 鉴权 / 会话 ----
  AUTH_INVALID_CREDENTIALS: '邮箱或密码错误',
  AUTH_LOCKED: '账号或 IP 已临时锁定，请稍后再试',
  AUTH_RATE_LIMITED: '尝试过于频繁，请稍后再试',
  AUTH_MFA_REQUIRED: '需要二次验证（2FA）',
  AUTH_MFA_INVALID: '动态验证码错误',
  AUTH_MFA_LOCKED: '二次验证已锁定，请稍后再试',
  AUTH_BACKUP_CODE_INVALID: '备份码无效',
  AUTH_REFRESH_INVALID: '会话已过期，请重新登录',
  AUTH_SESSION_REVOKED: '会话已失效，请重新登录',
  AUTH_REQUIRED_MFA: '请先完成二次验证',
  AUTH_INVITE_INVALID: '邀请链接无效或已过期',
  AUTH_INVITE_EMAIL_TAKEN: '该邮箱已被注册',
  AUTH_PASSWORD_RESET_INVALID: '密码重置链接无效或已过期',

  // ---- App ----
  APP_CONFLICT: 'App 配置冲突',
  APP_INACTIVE: 'App 未启用',

  // ---- 支付页面（ADM-PAY-S004）----
  PAY_PAGE_BUILT_IN_LOCKED: '系统预置支付页面不可删除，仅可上下架',
  // 下架页禁写 placement（A8 §7.5 ④）：运营可触达，必须给出可执行指引而非后端英文原文
  PAY_PAGE_INACTIVE: '该支付页当前已下架，无法配置渠道 placement · 请先上架页面再配置',
  PAY_PAGE_SCENARIO_REFERENCED: '该页面 scenario 仍被生效中的运营活动 / 推送引用，请先清理后再删除',
  // ---- 运营支付活动 offer（ADM-PAY-S005）----
  PAY_OFFER_REFERENCED: '该活动仍被生效中的 Banner / 未投递推送引用，请先下架引用方后再停用 / 删除',
  // Stripe 支付方式同步（ADM-PAY-S013，A8 §7.8.9）：具体 reason 由支付方式图标面板的同步状态行展开
  PAY_STRIPE_PAYMENT_METHOD_SYNC_FAILED: 'Stripe 支付方式同步失败，已保留上次同步结果',
  // 清空运营弹窗最后一个已配渠道时的拒绝码（A8 §7.5「operational_popup 分支」）：指引两条出路
  PAY_POPUP_PLACEMENT_REFERENCED:
    '运营弹窗至少要保留一个已配置渠道 · 仍有上架中的弹窗场景依赖该落点，请先在「运营管理 › 运营弹窗」下架场景，或保留另一渠道的 placement',
  // ---- Banner 轮播图（ADM-OPS-S004 / #228 决议 3）----
  OPS_BANNER_OFFER_NOT_FOUND: '所选运营活动不存在或未启用（offer 须为该 App 下生效中的活动）',

  // ---- 书籍 / 章节 ----
  BOOK_DELIST_REASON_REQUIRED: '下架需填写原因',
  BOOK_SOURCE_IDENTITY_LOCKED:
    '书籍已有章节或语言版本，来源信息已锁定；如需换源请新建书组并走迁移流程',
  BOOK_FREE_CHAPTER_LOCKED: '免费章节不可设为付费',
  BOOK_CHAPTER_PUBLISH_DEPENDENCY: '章节发布存在前置依赖',

  // ---- 反馈 ----
  FEEDBACK_CLOSED: '该反馈已关闭',
  FEEDBACK_NOT_REPLYABLE: '该反馈当前状态不可回复（如已解决）',
  FEEDBACK_TRANSLATION_FAILED: '翻译失败，请稍后再试',

  // ---- 财务 ----
  FINANCE_BONUS_AMOUNT_INVALID: 'BONUS 金额不合法',
  FINANCE_BONUS_REASON_LOCALE_REQUIRED: 'BONUS 发放原因需填写对应语言',
  FINANCE_IMPORT_ATTRIBUTION_CONFLICT:
    '该交易已归属或效果已投影，无法改绑到其他用户；请刷新队列后确认',

  // ---- 运营 ----
  OPS_BANNER_LOCALE_REQUIRED: 'Banner 需填写对应语言版本',
  OPS_HOME_SECTION_DEFAULT_LOCKED: '系统默认区块不可修改',
  // 启用运营弹窗场景时所选支付通道缺统一 Paywall placement（ADM-OPS-S007，与 PAY_PAGE_SCENARIO_REFERENCED 对偶）
  POPUP_SCENE_PLACEMENT_MISSING:
    '所选支付通道尚未配置运营弹窗 Paywall placement，请先在该 App 配置并启用后再启用场景',

  // ---- 推送 ----
  PUSH_TASK_TYPE_NOT_MANUAL: '该推送类型不支持手动创建',
  // 码名含 LOCALE 是历史命名；实际语义 = 该 task_type 映射的模板未配置（dev-docs 21-Admin错误码段 §2.1），非缺语言
  PUSH_TEMPLATE_LOCALE_REQUIRED: '该推送类型的模板未配置，请先配置对应类型的推送模板',
  PUSH_OFFER_NOT_FOUND: '所选运营活动不存在或未启用（offer 须为该 App 下投递时刻生效中的活动）',
  PUSH_TEST_SEND_NOT_SUPPORTED:
    '该模板当前不可测试发送（需要真实业务事件的模板不支持；营销模板须先启用）',
  PUSH_TEST_RECIPIENT_NOT_ELIGIBLE:
    '收件人不是该 App 的内部测试账号，请先在用户详情为其打「内部测试专用账号」标签',

  // ---- 用户 ----
  USER_ALREADY_BANNED: '该用户已封禁',
  USER_NOT_BANNED: '该用户未封禁',
  USER_DEVICE_ALREADY_WHITELISTED: '该设备已在白名单',
  USER_TAG_RULE_UNBOUND: '该标签尚未绑定分群规则，不能启用',
};

/** 取错误码对应中文文案；未登记则回退后端原 message */
export function zhErrorMessage(code: string, fallback?: string): string {
  return ADMIN_ERROR_ZH[code] ?? fallback ?? '操作失败，请稍后再试';
}
